/* eslint-disable react/display-name */
import React, { useMemo, useState } from 'react'
import PlatformLayout from 'src/components/Layout/Platform'
import MenuModules from 'src/components/Common/MenuModules'
import ReactPlayer from 'react-player'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import { useTranslation } from 'react-i18next'
import { IExercises, ILessonExtended } from 'src/service/api/api.types'
import { useDispatch, useSelector } from 'react-redux'
import BoxTeacher from 'src/components/Curso/Classroom/BoxTeacher'
import { Button } from 'src/components/Common/Button'
import Comments from 'src/components/Curso/Classroom/Comments'
import { apiPrivate } from 'src/service/api/api.private'
import { getLessonRequest, setTabIndex } from 'src/store/lessons'
import LessonPrivate from 'src/components/Curso/Classroom/LessonPrivate'
import { State } from 'src/store/rootReducers'
import { ContentClassroomSkeleton } from 'src/components/Skeleton/contentClassroom'
import { HiDownload } from 'react-icons/hi'
import formatText from 'src/utils/formatText'
import {
  ClassroomDesc,
  Container,
  Content,
  TabsArea,
  ExercisesContainer,
  ButtonsWrapper,
} from './styles'

interface ExerciseProps {
  exercises: IExercises
}

interface Props {
  module: ILessonExtended
}

const LessonContent: React.FC<Props> = ({ module }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const profile = useSelector((state: State) => state.user.profile)
  const isAuthenticated = useSelector((state: State) => state.user.isAuthenticated)
  const [isLoading] = useState(false)
  const tabIndex = useSelector((state: State) => state.course.tabIndex)

  const notAuthorized = isAuthenticated === false && !module.isPublic
  const planExpired = Number(profile?.plans?.length) === 0 && !module.isPublic
  let hasAccess = false

  if (profile !== undefined && isAuthenticated !== undefined) {
    hasAccess = !notAuthorized && !planExpired
  }

  async function handleWatched() {
    await apiPrivate.setToggleWatch(module.id)
    dispatch(getLessonRequest(module.slug))
  }

  async function handleFavorite() {
    await apiPrivate.setToggleFavorite(module.id)
    dispatch(getLessonRequest(module.slug))
  }

  const ExercisesCardRender = React.memo(
    Object.assign(
      ({ exercises }: ExerciseProps) => (
        <>
          <h2>{exercises.title}</h2>
          <p>{formatText(exercises.description)}</p>
          {exercises.fileUrl ? (
            <Button
              preset="exerciseDownload"
              isLink
              target="_blank"
              LeftAccessory={<HiDownload />}
              labelTx="classroom.exercises.downloadText"
              href={exercises.fileUrl}
            />
          ) : null}
        </>
      ),
      { displayName: 'ExercisesCardRender' },
    ),
  )

  const Exercises = useMemo(
    () => () =>
      module.exercises.map(item => (
        <ExercisesContainer key={item.id}>
          <ExercisesCardRender exercises={item} />
        </ExercisesContainer>
      )),
    [module?.exercises],
  )

  const RenderPlayer = useMemo(
    () => () => {
      if (!hasAccess) {
        return <LessonPrivate showPlans={!notAuthorized} />
      }

      return (!module.isPublic && profile) || module.isPublic ? (
        <ReactPlayer
          className="react-player"
          controls
          url={module.video}
          width="100%"
          height="100%"
        />
      ) : null
    },
    [module.video, isAuthenticated],
  )

  const comments = useMemo(() => <Comments comments={module.comments} />, [module.comments])

  return (
    <PlatformLayout>
      <Container>
        <MenuModules category={module.category} />

        <Content>
          {isLoading ? (
            <ContentClassroomSkeleton />
          ) : (
            <Tabs
              selectedIndex={tabIndex}
              onSelect={index => dispatch(setTabIndex(index))}
              forceRenderTabPanel={false}
            >
              <TabList>
                {module.hasVideo ? <Tab>{t('classroom.lessonTab')}</Tab> : null}

                {module.exercises && module.exercises.length > 0 ? (
                  <Tab>{t('classroom.exercisesTab')}</Tab>
                ) : null}
              </TabList>

              <TabsArea>
                {!module.hasVideo && !module.exercises && isAuthenticated === false ? (
                  <TabPanel>
                    <div className="player-wrapper">
                      <LessonPrivate showPlans={!isAuthenticated} />
                    </div>
                  </TabPanel>
                ) : null}

                {module.hasVideo ? (
                  <TabPanel>
                    <div className="player-wrapper">
                      <RenderPlayer />
                    </div>

                    <ButtonsWrapper>
                      {hasAccess && (
                        <Button
                          onClick={() => handleFavorite()}
                          preset={module.wasFavorite === true ? 'cancelFavorite' : 'favorite'}
                          labelTx={
                            module.wasFavorite === true
                              ? 'classroom.favorite.cancel'
                              : 'classroom.favorite.mark'
                          }
                        />
                      )}
                      {hasAccess && (
                        <Button
                          onClick={() => handleWatched()}
                          preset={module.wasWatched === true ? 'cancelView' : 'lessonConclude'}
                          labelTx={
                            module.wasWatched === true
                              ? 'classroom.VisibleBtn.notVisible'
                              : 'classroom.VisibleBtn.visible'
                          }
                        />
                      )}
                    </ButtonsWrapper>

                    <section>
                      <ClassroomDesc>
                        <div className="title-area">
                          <h2>{module.title}</h2>
                        </div>
                        {formatText(module.description)}
                      </ClassroomDesc>

                      <div className="box-teacher-container">
                        <BoxTeacher />
                      </div>
                    </section>

                    {hasAccess && (
                      <section id="helper">
                        <h4>Tire sua dúvidas</h4>
                        {comments}
                      </section>
                    )}
                  </TabPanel>
                ) : null}

                {module.exercises && module.exercises.length > 0 ? (
                  <TabPanel>{Exercises()}</TabPanel>
                ) : null}
              </TabsArea>
            </Tabs>
          )}
        </Content>
      </Container>
    </PlatformLayout>
  )
}

export default LessonContent
